import React, { useState, useEffect, useMemo } from 'react';
import { 
  Copy, 
  Share2, 
  Gift, 
  Users, 
  Calendar,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { logEvent } from 'firebase/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { SectionHeader } from '../../components/ui';
import { analytics } from '../../config/firebase';

import { buildInviteUrl } from '../../constants/inviteLinks';
import { loadReferralStats } from '../../services/referralStatsService';

const ReferralProgram: React.FC = () => {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [copiedKind, setCopiedKind] = useState<'code' | 'link' | 'share' | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [totalReferrals, setTotalReferrals] = useState<number | null>(null);
  const [freeMonthsEarned, setFreeMonthsEarned] = useState<number | null>(null);
  const { strings, language, isRTL } = useLocalization();

  const inviteUrl = useMemo(() => buildInviteUrl(referralCode), [referralCode]);

  useEffect(() => {
    try {
      logEvent(analytics, 'referral_entry_opened', { surface: 'web' });
    } catch {
      // Analytics optional — never block UI
    }
  }, []);

  const refreshStats = async (uid: string) => {
    setStatsLoading(true);
    setStatsError(false);
    try {
      const stats = await loadReferralStats(uid);
      setTotalReferrals(stats.successfulCount);
      setFreeMonthsEarned(stats.monthsEarned);
    } catch (err) {
      console.error('Referral stats failed', err);
      setStatsError(true);
      setTotalReferrals(null);
      setFreeMonthsEarned(null);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      setReferralCode(user.id);
      void refreshStats(user.id);
    }
  }, [user?.id]);

  const flashCopied = (kind: 'code' | 'link' | 'share') => {
    setCopiedKind(kind);
    setTimeout(() => setCopiedKind(null), 2000);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      flashCopied('code');
    } catch (err) {
      console.error('Failed to copy referral code:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      flashCopied('link');
    } catch (err) {
      console.error('Failed to copy invite link:', err);
    }
  };

  const handleShareCode = async () => {
    const shareText = `${strings.referral.shareMessage} ${referralCode}\n\n${
      language === 'ar' ? 'رابط الانضمام:' : 'Join here:'
    } ${inviteUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: strings.referral.shareTitle,
          text: shareText,
          url: inviteUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        flashCopied('share');
      } catch (err) {
        console.error('Failed to copy share text:', err);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <SectionHeader
          title={strings.referral.title}
          description={strings.referral.headerDescription}
          language={language}
          isRTL={isRTL}
        />

        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-8 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full mb-6 shadow-lg">
              <Gift className="w-12 h-12 text-white" aria-hidden />
            </div>
            <h2 className="text-3xl font-bold text-amber-600 dark:text-amber-400 mb-2">
              {language === 'ar' ? 'مرافعة' : 'Morafaa'}
            </h2>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {strings.referral.subtitle}
            </h3>
          </div>

          <div className="p-8">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-6 mb-8">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {strings.referral.howItWorks}
              </h4>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {n}
                    </div>
                    <p>
                      {n === 1 && strings.referral.step1}
                      {n === 2 && strings.referral.step2}
                      {n === 3 && strings.referral.step3}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {strings.referral.yourCode}
              </h4>

              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-4 mb-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <code className="text-lg font-mono text-gray-900 dark:text-white break-all">
                    {referralCode || '—'}
                  </code>
                  {copiedKind === 'code' && (
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <CheckCircle className="w-5 h-5 mr-1" aria-hidden />
                      <span className="text-sm font-medium">{strings.referral.copied}</span>
                    </div>
                  )}
                </div>
              </div>

              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {strings.referral.yourInviteLink}
              </h4>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-4 mb-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <code className="text-sm font-mono text-gray-900 dark:text-white break-all">
                    {inviteUrl}
                  </code>
                  {copiedKind === 'link' && (
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <CheckCircle className="w-5 h-5 mr-1" aria-hidden />
                      <span className="text-sm font-medium">{strings.referral.copied}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={handleCopyCode}
                  className="flex items-center justify-center space-x-2 p-4 bg-amber-500 text-white rounded-2xl hover:bg-amber-600 transition-all duration-300 shadow-lg min-h-[48px]"
                >
                  <Copy className="w-5 h-5" aria-hidden />
                  <span className="font-semibold">{strings.referral.copy}</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="flex items-center justify-center space-x-2 p-4 bg-amber-500 text-white rounded-2xl hover:bg-amber-600 transition-all duration-300 shadow-lg min-h-[48px]"
                >
                  <Copy className="w-5 h-5" aria-hidden />
                  <span className="font-semibold">{strings.referral.copyLink}</span>
                </button>

                <button
                  type="button"
                  onClick={handleShareCode}
                  className="flex items-center justify-center space-x-2 p-4 bg-primary text-white rounded-2xl hover:opacity-90 transition-all duration-300 shadow-lg min-h-[48px]"
                >
                  <Share2 className="w-5 h-5" aria-hidden />
                  <span className="font-semibold">{strings.referral.share}</span>
                </button>
              </div>
              {copiedKind === 'share' && (
                <p className="mt-3 text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" aria-hidden />
                  {strings.referral.copied}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {statsError ? (
                <p className="md:col-span-2 text-sm text-red-600 dark:text-red-400">
                  {strings.referral.statsError}
                </p>
              ) : (
                <>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <Users className="w-6 h-6 text-green-600 dark:text-green-400" aria-hidden />
                      <h5 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {strings.referral.totalReferrals}
                      </h5>
                    </div>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {statsLoading || totalReferrals === null ? '…' : totalReferrals}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {strings.referral.successfulReferrals}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-primary/10 dark:from-purple-900/20 dark:to-primary/20 rounded-2xl p-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" aria-hidden />
                      <h5 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {strings.referral.freeMonthsEarned}
                      </h5>
                    </div>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                      {statsLoading || freeMonthsEarned === null ? '…' : freeMonthsEarned}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {strings.referral.monthsOfFreeService}
                    </p>
                  </div>
                  {!statsLoading && totalReferrals === 0 && freeMonthsEarned === 0 && (
                    <p className="md:col-span-2 text-sm text-gray-600 dark:text-gray-400">
                      {strings.referral.statsEmpty}
                    </p>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => user?.id && void refreshStats(user.id)}
              className="mt-4 text-sm font-semibold text-primary"
            >
              {strings.referral.statsRefresh}
            </button>

            <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" aria-hidden />
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p className="font-semibold mb-2">{strings.referral.termsTitle}</p>
                  <ul className="space-y-1 list-disc list-inside">
                    {strings.referral.terms.map((term, index) => (
                      <li key={index}>{term}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram;
